<template>
  <div
    v-if="invoice"
    class="box flex h-full flex-row divide-x divide-slate-200 overflow-hidden shadow-xl dark:divide-slate-600"
  >
    <!--#region left panel (Funeral Manager's InvoicingLeftPanel) -->
    <div class="flex w-64 shrink-0 flex-col">
      <div class="flex shrink-0 flex-col gap-2 px-5 py-3">
        <div class="text-2xl font-bold capitalize-first">
          {{ $t('global.invoicing_type.' + invoice.doc_type) }}
        </div>
        <h2 v-if="invoice.company" class="-mt-2 text-sm font-medium">
          {{ invoice.company.name }}
        </h2>
        <!-- Read only for a customer: same look, no switching. -->
        <StatusInput
          v-if="metadata?.status"
          :model-value="invoice.status"
          :options="metadata.status"
          :disabled="auth_role === 'customer-user' || !$can('manage_invoicing')"
          @status-changed="(status) => mChangeStatus({ id: status })"
        />
        <BaseLabel
          v-else
          :big="true"
          :content="labelContent"
          :color="labelColor"
        />
      </div>
      <div
        class="flex min-h-0 flex-1 flex-col divide-y overflow-auto px-5 pb-3"
      >
        <div class="flex flex-col gap-1 pb-3">
          <div v-if="invoice.work">
            <div
              class="truncate text-xs leading-tight font-medium text-gray-500 capitalize-first"
            >
              {{ $t('global.work') }}
            </div>
            <button
              class="text-left leading-tight font-medium text-primary-600 hover:underline"
              @click="
                $router.push({
                  name: 'work_form_edit_route',
                  params: { id: invoice.work_id }
                })
              "
            >
              {{ invoice.work.title ?? invoice.work.reference }}
            </button>
          </div>
          <div v-if="invoice.reference">
            <div
              class="truncate text-xs leading-tight font-medium text-gray-500 capitalize-first"
            >
              {{ $t('attributes.reference') }}
            </div>
            <div
              class="leading-tight font-medium text-gray-900 dark:text-gray-100"
            >
              {{ invoice.reference }}
            </div>
          </div>
          <div v-if="invoice.reference_extern">
            <div
              class="truncate text-xs leading-tight font-medium text-gray-500 capitalize-first"
            >
              {{ $t('attributes.reference_extern') }}
            </div>
            <div
              class="leading-tight font-medium text-gray-900 dark:text-gray-100"
            >
              {{ invoice.reference_extern }}
            </div>
          </div>
          <div>
            <div
              class="truncate text-xs leading-tight font-medium text-gray-500 capitalize-first"
            >
              {{ $t('attributes.date') }}
            </div>
            <div
              class="leading-tight font-medium text-gray-900 dark:text-gray-100"
            >
              {{ invoice.emission_date }}
            </div>
          </div>
          <div>
            <div
              class="truncate text-xs leading-tight font-medium text-gray-500 capitalize-first"
            >
              {{ $t('attributes.due_date') }}
            </div>
            <div
              class="leading-tight font-medium text-gray-900 dark:text-gray-100"
            >
              {{ invoice.due_date }}
            </div>
          </div>
          <PeppolSendInvoiceComponent
            v-if="showPeppol"
            :invoice="invoice"
            class="mt-2"
          />
        </div>

        <div v-if="invoice.customer" class="flex flex-col gap-0.5 py-3">
          <h3 class="text-lg font-medium">{{ invoice.customer.name }}</h3>
          <div>{{ invoice.customer.street }}</div>
          <div>
            {{ invoice.customer.post_code }} {{ invoice.customer.city }}
          </div>
        </div>

        <div v-if="invoice.note_intern" class="py-3">
          <div
            class="truncate text-xs leading-tight font-medium text-gray-500 capitalize-first"
          >
            {{ $t('attributes.internal_note') }}
          </div>
          <div
            class="leading-tight font-medium whitespace-pre-line text-gray-900 dark:text-gray-100"
          >
            {{ invoice.note_intern }}
          </div>
        </div>

        <div class="flex flex-col gap-1 py-3">
          <div>
            <div class="truncate leading-tight text-gray-500 capitalize-first">
              {{ $t('attributes.total_tvac') }}
            </div>
            <div
              class="text-2xl leading-tight font-bold text-gray-900 dark:text-gray-100"
            >
              {{ money(invoice.total_tvac) }}
            </div>
          </div>
          <div>
            <div class="truncate leading-tight text-gray-500 capitalize-first">
              {{ $t('attributes.outstanding_balance') }}
            </div>
            <div
              class="text-2xl leading-tight font-bold text-gray-900 dark:text-gray-100"
            >
              {{ money(invoice.solde) }}
            </div>
          </div>
          <div>
            <div
              class="truncate text-xs leading-tight font-medium text-gray-500 capitalize-first"
            >
              {{ $t('attributes.total_htva') }}
            </div>
            <div
              class="leading-tight font-medium text-gray-900 dark:text-gray-100"
            >
              {{ money(invoice.total_htva) }}
            </div>
          </div>
          <div v-if="invoice.total_hors_debours !== undefined">
            <div
              class="truncate text-xs leading-tight font-medium text-gray-500 capitalize-first"
            >
              {{ $t('attributes.total_hors_debours') }}
            </div>
            <div
              class="leading-tight font-medium text-gray-900 dark:text-gray-100"
            >
              {{ money(invoice.total_hors_debours) }}
            </div>
          </div>
          <div>
            <div
              class="truncate text-xs leading-tight font-medium text-gray-500 capitalize-first"
            >
              {{ $t('attributes.payments') }}
            </div>
            <button
              v-if="auth_role !== 'customer-user'"
              class="leading-tight font-medium text-primary-600 hover:underline"
              @click="openModal('InvoiceShowPaiments')"
            >
              {{ money(invoice.total_paid) }}
            </button>
            <div
              v-else
              class="leading-tight font-medium text-gray-900 dark:text-gray-100"
            >
              {{ money(invoice.total_paid) }}
            </div>
          </div>
        </div>

        <div v-if="vatDistribution.length" class="flex flex-col gap-1 pt-3">
          <div
            class="truncate text-xs leading-tight font-medium text-gray-500 capitalize-first"
          >
            {{ $t('attributes.vat_distribution') }}
          </div>
          <div
            v-for="distribution in vatDistribution"
            :key="distribution.vat_rate"
            class="grid grid-cols-3 gap-2 text-xs"
          >
            <span>{{ distribution.vat_rate }}%</span>
            <span class="text-right">
              {{ $h.formatCurrency(distribution.total_vat_excluded) }}
            </span>
            <span class="text-right font-medium">
              {{
                distribution.vat_rate !== 0
                  ? $h.formatCurrency(distribution.vat_amount)
                  : ''
              }}
            </span>
          </div>
        </div>
      </div>
    </div>
    <!--#endregion -->

    <!--#region right -->
    <div class="flex h-full max-h-full min-h-0 flex-1 flex-col">
      <!-- top menu (Funeral Manager's TheInvoicingShowTopMenu) -->
      <div class="mx-3 flex shrink-0 justify-between gap-x-2 border-b py-3">
        <div class="flex flex-row items-center gap-x-2">
          <BaseButton
            color="dark"
            icon="long-arrow-left"
            size="small"
            :title="$t('button.back')"
            @click="$router.go(-1)"
          />
          <BaseButton
            v-if="invoice.work"
            icon="folder-open"
            size="small"
            secondary-color
            :title="$t('button.to_m', { var: $t('global.work') })"
            @click="
              $router.push({
                name: 'work_form_edit_route',
                params: { id: invoice.work_id }
              })
            "
          />
        </div>
        <div class="flex flex-row items-center justify-end gap-x-2">
          <BaseButton
            v-if="auth_role !== 'customer-user'"
            icon="credit-card"
            size="small"
            :title="$t('global.add_payment')"
            @click="openModal('InvoiceAddPaiment')"
          />
          <BaseButton
            icon="print"
            size="small"
            :title="$t('global.print')"
            @click="
              $router.push({
                name: 'invoice_print_route',
                params: { invoicing_id: invoice.id }
              })
            "
          />
          <!-- The API refuses an invoice's activity log to a customer. -->
          <BaseButton
            v-if="
              $route.name === 'invoice_info_route' &&
              auth_role !== 'customer-user'
            "
            icon="file-alt"
            size="small"
            color="light"
            :title="$t('global.activities')"
            @click="
              $router.push({
                name: 'invoice_activity_route',
                params: { invoicing_id: invoice.id }
              })
            "
          />
          <InvoicingActionDropDownComponent
            v-if="auth_role !== 'customer-user'"
            @button-send="openModal('InvoicingSendDialog')"
            @change-item-order="openModal('ChangeItemOrderDialog')"
          />
        </div>
      </div>

      <div class="flex flex-1 overflow-y-auto bg-white p-3 dark:bg-slate-800">
        <div
          class="mx-auto flex w-full flex-col gap-6 px-0 pb-3 xl:px-8 xxl:px-16"
        >
          <InfoPanel
            v-if="invoice.peppol_locked"
            type="info"
            :description="$t('global.peppol.locked')"
          />
          <div class="rounded border border-gray-100 p-3 shadow">
            <InvoicingItemsTable
              v-if="invoice.invoice_items"
              :invoice_items="invoice.invoice_items"
              :invoice-is-not-established="linesEditable"
              @event-click="editInvoiceItem"
            />
          </div>

          <div class="flex flex-row gap-6">
            <div class="w-1/2">
              <div
                v-if="
                  linesEditable &&
                  auth_role !== 'customer-user' &&
                  $can('manage_invoicing')
                "
                class="flex flex-row space-x-3"
              >
                <div class="flex-1">
                  <BaseSelectSimpleAjax
                    :key="selectKey"
                    v-model="ajoutRapide"
                    :group-class="'mt-0'"
                    url="api/item-for-select"
                    select-label="id"
                  />
                </div>
                <div>
                  <BaseButton
                    :title="
                      $t('button.browse_f', { var: $t('global.item_list') })
                    "
                    icon="list-ul"
                    @click="openModal('InvoiceItemAddDialog')"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!--#endregion -->
  </div>
  <!--#region Dialogs -->
  <InvoiceItemEditDialog
    @backdropClicked="closeAllModals"
    :invoiceItemId="dataModal"
    @close="closeAllModals"
    :is-modal-visible="isModalVisible['InvoiceItemEdit']"
  ></InvoiceItemEditDialog>
  <InvoicingShowPaimentsDialog
    @backdropClicked="closeAllModals"
    @close="closeAllModals"
    @switchToPayment="switchToPayment"
    :is-modal-visible="isModalVisible['InvoiceShowPaiments']"
  ></InvoicingShowPaimentsDialog>
  <InvoicingAddPaimentDialog
    @backdropClicked="closeAllModals"
    @close="closeAllModals"
    :is-modal-visible="isModalVisible['InvoiceAddPaiment']"
  ></InvoicingAddPaimentDialog>
  <!-- It loads the whole item catalogue: only for who may add lines. -->
  <invoiceItemAddDialog
    v-if="auth_role !== 'customer-user' && $can('manage_invoicing')"
    @backdropClicked="closeAllModals"
    @close="closeAllModals"
    :is-modal-visible="isModalVisible['InvoiceItemAddDialog']"
  />
  <InvoicingSendDialog
    @backdropClicked="closeAllModals"
    @close="closeAllModals"
    :is-modal-visible="isModalVisible['InvoicingSendDialog']"
  />
  <ChangeItemOrderDialog
    v-if="invoice"
    @backdropClicked="closeAllModals"
    @close="closeAllModals"
    :is-modal-visible="isModalVisible['ChangeItemOrderDialog']"
  />

  <!--#endregion -->
</template>

<script>
import StatusInput from '@c/addf-package/components/status/StatusInput.vue'
import InfoPanel from '@c/baseComponents/InfoPanel.vue'
import PeppolSendInvoiceComponent from '@c/Partner/Peppol/PeppolSendInvoiceComponent.vue'
import { mapActions, mapGetters } from 'vuex'

import ChangeItemOrderDialog from '../dialogs/ChangeItemOrderDialog'
import invoiceItemAddDialog from '../dialogs/invoiceItemAddDialog'
import InvoiceItemEditDialog from '../dialogs/InvoiceItemEditDialog'
import InvoicingActionDropDownComponent from './components/InvoicingActionDropDownComponent'
import InvoicingAddPaimentDialog from '../dialogs/InvoicingAddPaimentDialog'
import InvoicingItemsTable from '../tables/InvoicingItemsTable'
import InvoicingSendDialog from '../dialogs/InvoicingSendDialog'
import InvoicingShowPaimentsDialog from '../dialogs/InvoicingShowPaimentsDialog'

export default {
  name: 'InvoicingInfo',
  components: {
    StatusInput,
    InfoPanel,
    PeppolSendInvoiceComponent,
    ChangeItemOrderDialog,
    invoiceItemAddDialog,
    InvoiceItemEditDialog,
    InvoicingActionDropDownComponent,
    InvoicingAddPaimentDialog,
    InvoicingItemsTable,
    InvoicingSendDialog,
    InvoicingShowPaimentsDialog
  },
  props: {
    invoicing_id: {
      type: String,
      required: true
    },
    id: {
      type: String,
      required: false
    }
  },
  emits: ['refreshWork'],
  data() {
    return {
      fakeItemToReplaceByMetadata: { un: 1, deux: 2, trois: 3 },
      dataModal: null,
      selectKey: 0,
      ajoutRapide: null,
      isModalVisible: {
        InvoiceItemEdit: false,
        InvoiceShowPaiments: false,
        InvoiceAddPaiment: false,
        InvoiceItemAddDialog: false,
        InvoicingSendDialog: false,
        ChangeItemOrderDialog: false
      }
    }
  },
  computed: {
    ...mapGetters({
      invoice: 'invoicing/getInvoice',
      metadata: 'invoicing/getInvoiceMetadata',
      auth: 'auth/getAuth'
    }),
    labelContent() {
      return this.$t(`enums.invoicing.status.${this.invoice.status}`)
    },
    labelColor() {
      return (
        this.metadata &&
        this.metadata?.status?.find(
          (status) => status.id === this.invoice.status
        ).bg_color
      )
    },
    auth_role() {
      return this.auth.roles[0]
    },
    vatDistribution() {
      return [...(this.invoice.vat_distribution ?? [])].sort(
        (a, b) => a.vat_rate - b.vat_rate
      )
    },
    invoiceIsNotEstablished() {
      return !(this.invoice.status === 'EST')
    },
    // Sent on Peppol and not rejected: the back refuses any change to the
    // lines (a credit note corrects it).
    linesEditable() {
      return this.invoiceIsNotEstablished && !this.invoice.peppol_locked
    },
    /**
     * Staff only, an invoice or credit note past draft and not cancelled,
     * from a company sending on Peppol.
     */
    showPeppol() {
      return (
        this.auth_role !== 'customer-user' &&
        ['INV', 'CN'].includes(this.invoice.doc_type) &&
        !['DRA', 'CAN'].includes(this.invoice.status) &&
        !!this.invoice.company?.peppol_integrator
      )
    }
  },
  methods: {
    /** An amount from the API (a number), in euros; '-' when absent. */
    money(value) {
      if (value === null || value === undefined || value === '') return '-'
      return this.$h.formatCurrency(value)
    },
    ...mapActions({
      fetchInvoiceID: 'invoicing/fetchInvoiceID',
      postInvoiceItem: 'invoicing/storeInvoiceItem',
      changeInvoiceStatus: 'invoicing/changeInvoiceStatus'
    }),
    mChangeStatus(newStatus) {
      this.changeInvoiceStatus({
        invoice_id: this.invoice.id,
        form_data: {
          status: newStatus.id
        }
      }).then(() =>
        this.$h.toastSuccess(
          this.$t('toasts.updated_f', { var: this.$t('global.invoice') })
        )
      )
    },
    switchToPayment() {
      this.closeAllModals()
      this.isModalVisible.InvoiceAddPaiment = true
    },
    editInvoiceItem(id) {
      this.dataModal = id
      this.openModal('InvoiceItemEdit')
    },
    addNewItem(item_id) {
      this.postInvoiceItem({ invoiceId: this.invoice.id, item_id: item_id })
      // Lets the same item be picked again right away.
      this.ajoutRapide = null
      this.selectKey++
    },
    openModal(property) {
      this.isModalVisible[property] = true
    },
    closeAllModals() {
      for (const property in this.isModalVisible) {
        this.isModalVisible[property] = false
      }
    }
  },
  watch: {
    ajoutRapide(newValue) {
      if (newValue) this.addNewItem(newValue)
    }
  },
  async beforeMount() {
    await this.fetchInvoiceID({ id: this.$route.params.invoicing_id })
  }
}
</script>
